import type { RoomCredentials, ViewRole } from "../types";

const STORAGE_KEY = "cue-studio-client-identities-v1";
const LIMIT = 32;
const ID = /^[a-zA-Z0-9_-]{16,80}$/;
type Identity = { roomId: string; relayUrl: string; role: ViewRole; id: string };
type DocumentIdentities = { current: Map<string, string>; retained: Identity[] };
const documents = new WeakMap<Document, DocumentIdentities>();

function mayResumeIdentity(): boolean {
  try {
    if ((document as Document & { wasDiscarded?: boolean }).wasDiscarded) return true;
    const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    return navigation?.type === "reload" || navigation?.type === "back_forward";
  } catch { return false; }
}

function readIdentities(): Identity[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw || raw.length > 32768) return [];
    const saved = JSON.parse(raw);
    if (saved?.version !== 1 || !Array.isArray(saved.identities)) return [];
    return saved.identities.slice(-LIMIT).filter((entry: Identity) =>
      entry && typeof entry.roomId === "string" && ID.test(entry.roomId) &&
      typeof entry.relayUrl === "string" && entry.relayUrl.length <= 2048 &&
      ["operator", "talent", "remote"].includes(entry.role) &&
      typeof entry.id === "string" && ID.test(entry.id))
      .map(({ roomId, relayUrl, role, id }: Identity) => ({ roomId, relayUrl, role, id }));
  } catch { return []; }
}

/**
 * Continue a reloaded/discarded tab's publisher lease. A new navigation always
 * starts a new identity: window.open and duplicated tabs can clone sessionStorage.
 * Only public room metadata is persisted; credentials never enter this record.
 */
export function clientIdentityForDocument(
  credentials: Pick<RoomCredentials, "roomId" | "relayUrl">,
  role: ViewRole,
  fresh: () => string,
): string {
  // Non-browser callers may represent several independent peers in one process.
  if (typeof document === "undefined") return fresh();
  let state = documents.get(document);
  if (!state) {
    state = { current: new Map(), retained: mayResumeIdentity() ? readIdentities() : [] };
    documents.set(document, state);
  }
  const namespace = { roomId: credentials.roomId, relayUrl: credentials.relayUrl || "", role };
  const key = JSON.stringify(namespace);
  const existing = state.current.get(key);
  if (existing) return existing;
  const matches = (entry: Identity) => entry.roomId === namespace.roomId &&
    entry.relayUrl === namespace.relayUrl && entry.role === role;
  const id = state.retained.find(matches)?.id || fresh();
  state.current.set(key, id);
  if (state.current.size > LIMIT) state.current.delete(state.current.keys().next().value!);
  state.retained = [...state.retained.filter((entry) => !matches(entry)), { ...namespace, id }].slice(-LIMIT);
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, identities: state.retained })); }
  catch { /* In-memory continuity still works if session storage is unavailable. */ }
  return id;
}
